import { useState, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';
import { searchVehicleTypeFiltersV2, updateVehicleV2 } from 'store/vehicleSliceV2';
import { bulkUploadVehiclesV2 } from 'store/vendorSliceV2';
import { transformVehicleToAPIFormat } from 'pagesv2/vendorv2/create-vendorv2/CreateVendorPageV2.utils';
import {
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from 'pagesv2/vehiclesv2/create-vehiclev2/CreateVehiclePageV2.constants';
import { DATE_FORMAT } from './VehicleInformationStep.constants';
import {
	clearVehicleFields,
	validateEditingVehicle,
	updateVehicleInLocalState,
	isPersistedVehicle,
} from './VehicleInformationStep.helpers';
import {
	handleFileSelect,
	handleFileChange,
	handleFileUpload,
} from './VehicleInformationStep.fileUpload';

/**
 * Custom hook to manage all state and handlers for VehicleInformationStep
 * Extracts all business logic to reduce component complexity
 *
 * @param {boolean} disabled - Whether the form is disabled
 * @returns {object} - All state, refs, and handlers needed by the component
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export default function useVehicleInformationStepLogic(disabled) {
	const dispatch = useDispatch();
	const { id: vendorId } = useParams();
	const { setFieldValue, values } = useFormikContext();

	// Modal and preview states
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [editingVehicle, setEditingVehicle] = useState(null);
	const [filePreviewVisible, setFilePreviewVisible] = useState(false);
	const [previewFileKey, setPreviewFileKey] = useState(null);
	const [previewFileName, setPreviewFileName] = useState('');
	const [savingVehicle, setSavingVehicle] = useState(false);

	// Vehicle type states
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);

	// File upload states
	const [uploading, setUploading] = useState({
		rc_doc: false,
		insurance: false,
		puc_doc: false,
		consent_letter: false,
	});

	// Local file states for display
	const [rcDocFile, setRcDocFile] = useState(null);
	const [insuranceFile, setInsuranceFile] = useState(null);
	const [pucFile, setPucFile] = useState(null);
	const [consentLetterFile, setConsentLetterFile] = useState(null);

	// Bulk upload states
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
	const [bulkUploadFileKey, setBulkUploadFileKey] = useState(null);

	// File input refs
	const rcDocFileInputRef = useRef(null);
	const insuranceFileInputRef = useRef(null);
	const pucDocFileInputRef = useRef(null);
	const consentLetterFileInputRef = useRef(null);

	// Sync vehicles from form values
	const vehicles = Array.isArray(values.vehicles) ? values.vehicles : [];

	// Fetch vehicle types on component mount
	useEffect(() => {
		const fetchVehicleTypes = async () => {
			setLoadingVehicleTypes(true);
			try {
				const response = await dispatch(
					searchVehicleTypeFiltersV2({
						filter_by: { is_active: true },
						limit: 1000,
					})
				);
				if (response?.results) {
					setVehicleTypeOptions(response.results);
				}
			} catch (error) {
				console.error('Error fetching vehicle types:', error);
				message.error('Failed to load vehicle types');
			} finally {
				setLoadingVehicleTypes(false);
			}
		};

		fetchVehicleTypes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Restore file display states from Formik values (for navigation persistence)
	useEffect(() => {
		if (values.rc_document_key && !rcDocFile) {
			setRcDocFile({
				name: values.rc_document_key,
			});
		}
		if (values.insurance_document_key && !insuranceFile) {
			setInsuranceFile({
				name: values.insurance_document_key,
			});
		}
		if (values.puc_document_key && !pucFile) {
			setPucFile({
				name: values.puc_document_key,
			});
		}
		if (values.consent_letter_key && !consentLetterFile) {
			setConsentLetterFile({
				name: values.consent_letter_key,
			});
		}
	}, [
		values.rc_document_key,
		values.insurance_document_key,
		values.puc_document_key,
		values.consent_letter_key,
		rcDocFile,
		insuranceFile,
		pucFile,
		consentLetterFile,
	]);

	// File upload handlers
	const handleFileSelectClick = (fieldName, fileInputRef) => {
		handleFileSelect(fieldName, fileInputRef, disabled);
	};

	const handleFileChangeEvent = async (event, fieldName) => {
		const file = event.target.files[0];

		await handleFileChange(
			event,
			fieldName,
			disabled,
			dispatch,
			getSignedUrl,
			setFieldValue,
			setUploading,
			message
		);

		// Update local file state for display
		if (file) {
			if (fieldName === 'rc_doc') {
				setRcDocFile(file);
			} else if (fieldName === 'insurance') {
				setInsuranceFile(file);
			} else if (fieldName === 'puc_doc') {
				setPucFile(file);
			} else if (fieldName === 'consent_letter') {
				setConsentLetterFile(file);
			}
		}

		// Clear validation error for the uploaded field
		let fileKeyField = null;
		if (fieldName === 'rc_doc') {
			fileKeyField = 'rc_document_key';
		} else if (fieldName === 'insurance') {
			fileKeyField = 'insurance_document_key';
		} else if (fieldName === 'puc_doc') {
			fileKeyField = 'puc_document_key';
		} else if (fieldName === 'consent_letter') {
			fileKeyField = 'consent_letter_key';
		}

		if (fileKeyField && values.vehicleErrors?.[fileKeyField]) {
			const newErrors = { ...values.vehicleErrors };
			delete newErrors[fileKeyField];
			setFieldValue('vehicleErrors', newErrors);
			setFieldValue(`${fileKeyField}_touched`, false);
		}
	};

	const handleFileUploadClick = (fieldName) => {
		handleFileUpload(
			fieldName,
			{
				rc_doc: rcDocFileInputRef,
				insurance: insuranceFileInputRef,
				puc_doc: pucDocFileInputRef,
				consent_letter: consentLetterFileInputRef,
			},
			handleFileSelectClick
		);
	};

	const handleFileRemove = (fieldName) => {
		if (disabled) return;

		if (fieldName === 'rc_doc') {
			setRcDocFile(null);
			setFieldValue('rc_doc_file', null);
			setFieldValue('rc_document_key', null);
		} else if (fieldName === 'insurance') {
			setInsuranceFile(null);
			setFieldValue('insurance_file', null);
			setFieldValue('insurance_document_key', null);
		} else if (fieldName === 'puc_doc') {
			setPucFile(null);
			setFieldValue('puc_doc_file', null);
			setFieldValue('puc_document_key', null);
		} else if (fieldName === 'consent_letter') {
			setConsentLetterFile(null);
			setFieldValue('consent_letter_file', null);
			setFieldValue('consent_letter_key', null);
		}

		// Clear validation error for the removed field
		let fileKeyField = null;
		if (fieldName === 'rc_doc') {
			fileKeyField = 'rc_document_key';
		} else if (fieldName === 'insurance') {
			fileKeyField = 'insurance_document_key';
		} else if (fieldName === 'puc_doc') {
			fileKeyField = 'puc_document_key';
		} else if (fieldName === 'consent_letter') {
			fileKeyField = 'consent_letter_key';
		}

		if (fileKeyField && values.vehicleErrors?.[fileKeyField]) {
			const newErrors = { ...values.vehicleErrors };
			delete newErrors[fileKeyField];
			setFieldValue('vehicleErrors', newErrors);
			setFieldValue(`${fileKeyField}_touched`, false);
		}
	};

	// Bulk upload handlers
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	const handleBulkUploadFileUpload = async (file) => {
		console.log('📁 [VehicleInformationStep] Starting bulk upload file upload to S3:', file);

		try {
			const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
			const fileName = `vehicles_${timestamp}.csv`;

			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'vehicle_bulk_upload_v2',
						keys: [fileName],
					},
				})
			);

			console.log('✅ [VehicleInformationStep] Signed URL response:', signedUrlResponse);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			const uploadResponse = await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`Upload failed with status: ${uploadResponse.status}`);
			}

			console.log('✅ [VehicleInformationStep] File uploaded to S3 successfully');

			setBulkUploadFileKey(fileData.filename);
			message.success('File uploaded successfully');
		} catch (error) {
			console.error('❌ [VehicleInformationStep] File upload failed:', error);
			message.error('Failed to upload file. Please try again.');
			throw error;
		}
	};

	const handleBulkUploadSubmit = async () => {
		console.log(
			'📤 [VehicleInformationStep] Submitting bulk upload with file key:',
			bulkUploadFileKey
		);

		if (!bulkUploadFileKey) {
			message.error('Please upload a file first');
			return;
		}

		setBulkUploadLoading(true);

		try {
			const response = await dispatch(
				bulkUploadVehiclesV2({
					fileKey: bulkUploadFileKey,
					setErrors: (errors) => {
						console.error('❌ [VehicleInformationStep] Bulk upload errors:', errors);
						message.error(errors?.message || 'Bulk upload failed');
					},
				})
			);

			console.log('✅ [VehicleInformationStep] Bulk upload response:', response);

			if (response?.status === 'success') {
				setBulkUploadModalVisible(false);
				setBulkUploadFileKey(null);

				if (response.data?.download_url) {
					console.log(
						'⬇️ [VehicleInformationStep] Downloading result CSV:',
						response.data.download_url
					);
					window.open(response.data.download_url, '_blank');
				}

				const { success_count, error_count, total_count } = response.data || {};
				message.success(
					`Bulk upload completed: ${success_count} success, ${error_count} errors out of ${total_count} total`
				);
			}
		} catch (error) {
			console.error('❌ [VehicleInformationStep] Bulk upload submission failed:', error);
			message.error('Bulk upload failed. Please try again.');
		} finally {
			setBulkUploadLoading(false);
		}
	};

	const handleBulkUploadModalClose = () => {
		setBulkUploadModalVisible(false);
		setBulkUploadFileKey(null);
		setBulkUploadLoading(false);
	};

	// Vehicle management handlers
	const validateVehicleFieldsForSubmission = () => {
		const errors = {};
		const touched = {};

		const requiredFields = [
			{ field: 'vehicle_type_id', errorMessage: 'Vehicle Type is required' },
			{ field: 'vehicle_number', errorMessage: 'Vehicle Number is required' },
		];

		if (!values.skip_document_upload) {
			requiredFields.push(
				{ field: 'rc_document_key', errorMessage: 'RC Document is required' },
				{ field: 'rc_valid_till', errorMessage: 'RC Valid Till is required' },
				{ field: 'insurance_document_key', errorMessage: 'Insurance Document is required' },
				{ field: 'insurance_valid_till', errorMessage: 'Insurance Valid Till is required' },
				{ field: 'ownership_type', errorMessage: 'Ownership Type is required' }
			);
			if (values.gps_installed === undefined || values.gps_installed === null) {
				errors.gps_installed = 'Please select GPS installation status';
				touched.gps_installed = true;
			}

			if (values.gps_installed === true && !values.gps_provider_name) {
				errors.gps_provider_name = 'GPS Provider Name is required';
				touched.gps_provider_name = true;
			}
		}

		requiredFields.forEach(({ field, errorMessage }) => {
			if (!values[field]) {
				errors[field] = errorMessage;
				touched[field] = true;
			}
		});

		if (
			values.vehicle_number &&
			!errors.vehicle_number &&
			!VEHICLE_NUMBER_PATTERN.test(values.vehicle_number)
		) {
			errors.vehicle_number = VEHICLE_NUMBER_MESSAGE;
			touched.vehicle_number = true;
		}

		if (values.puc_document_key && !values.puc_valid_till) {
			errors.puc_valid_till = 'PUC Valid Till is required when PUC document is uploaded';
			touched.puc_valid_till = true;
		}

		if (
			!values.skip_document_upload &&
			values.ownership_type === 'Aggregator' &&
			!values.consent_letter_key
		) {
			errors.consent_letter_key = 'Consent Letter is required for Aggregator';
			touched.consent_letter_key = true;
		}

		return { errors, touched };
	};

	const handleAddVehicle = () => {
		if (disabled) return;

		const { errors, touched } = validateVehicleFieldsForSubmission();

		if (Object.keys(errors).length > 0) {
			setFieldValue('vehicleErrors', errors);
			Object.keys(touched).forEach((fieldName) => {
				setFieldValue(`${fieldName}_touched`, true);
			});
			return;
		}

		setFieldValue('vehicleErrors', {});

		// Get vehicle type name for display
		const selectedVehicleType = vehicleTypeOptions.find(
			(option) => option.id === values.vehicle_type_id
		);

		const newVehicle = {
			key: `vehicle_${Date.now()}`,
			vehicleType: selectedVehicleType?.label || '',
			vehicleNumber: values.vehicle_number || '',
			rcDocument: !!values.rc_document_key,
			insurance: !!values.insurance_document_key,
			ownershipType: values.ownership_type || '',
			vehicle_type_id: values.vehicle_type_id, // Store ID, not label
			vehicle_type_name: selectedVehicleType?.label || '', // Store name for display
			vehicle_number: values.vehicle_number,
			rc_document_key: values.rc_document_key,
			rc_valid_till: values.rc_valid_till
				? moment(values.rc_valid_till).format(DATE_FORMAT)
				: null,
			insurance_document_key: values.insurance_document_key,
			insurance_valid_till: values.insurance_valid_till
				? moment(values.insurance_valid_till).format(DATE_FORMAT)
				: null,
			puc_document_key: values.puc_document_key,
			puc_valid_till: values.puc_valid_till
				? moment(values.puc_valid_till).format(DATE_FORMAT)
				: null,
			ownership_type: values.ownership_type,
			consent_letter_key: values.consent_letter_key,
			gps_installed: values.gps_installed,
			gps_provider_name: values.gps_installed === true ? values.gps_provider_name : null,
			skip_document_upload: !!values.skip_document_upload,
		};

		const currentVehicles = Array.isArray(values.vehicles) ? values.vehicles : [];
		const updatedVehicles = [...currentVehicles, newVehicle];

		setFieldValue('vehicles', updatedVehicles);

		clearVehicleFields(setFieldValue, {
			rcDocFileInputRef,
			insuranceFileInputRef,
			pucDocFileInputRef,
			consentLetterFileInputRef,
		});

		// Clear local file states
		setRcDocFile(null);
		setInsuranceFile(null);
		setPucFile(null);
		setConsentLetterFile(null);
		setFieldValue('skip_document_upload', false);

		message.success('Vehicle added successfully');
	};

	const handleViewVehicle = (vehicle) => {
		setSelectedVehicle(vehicle);
		setEditingVehicle({
			vehicleType: vehicle.vehicleType || vehicle.vehicle_type_name || '',
			vehicleNumber: vehicle.vehicleNumber || vehicle.vehicle_number || '',
			vehicle_type_id: vehicle.vehicle_type_id || vehicle.vehicleTypeId || '',
			vehicle_number: vehicle.vehicle_number || vehicle.vehicleNumber || '',
			rc_document_key: vehicle.rc_document_key || '',
			rc_valid_till: vehicle.rc_valid_till
				? moment(vehicle.rc_valid_till, DATE_FORMAT)
				: null,
			insurance_document_key: vehicle.insurance_document_key || '',
			insurance_valid_till: vehicle.insurance_valid_till
				? moment(vehicle.insurance_valid_till, DATE_FORMAT)
				: null,
			puc_document_key: vehicle.puc_document_key || '',
			puc_valid_till: vehicle.puc_valid_till
				? moment(vehicle.puc_valid_till, DATE_FORMAT)
				: null,
			ownership_type: vehicle.ownership_type || vehicle.ownershipType || '',
			consent_letter_key: vehicle.consent_letter_key || '',
			gps_installed: vehicle.gps_installed,
			gps_provider_name: vehicle.gps_provider_name || '',
			skip_document_upload:
				!!vehicle.skip_document_upload || vehicle.documents_complete === false,
		});
		setModalVisible(true);
	};

	const handleModalClose = () => {
		setModalVisible(false);
		setSelectedVehicle(null);
		setEditingVehicle(null);
	};

	const handleSaveChanges = () => {
		if (!selectedVehicle) {
			message.error('No vehicle selected');
			return;
		}

		if (!editingVehicle) {
			message.error('No changes to save');
			return;
		}

		// Skip-document is only honored for newly-added (not yet persisted) vehicles.
		// For an already-saved vehicle the documents are always mandatory.
		if (
			!validateEditingVehicle(editingVehicle, message, !isPersistedVehicle(selectedVehicle))
		) {
			return;
		}

		setSavingVehicle(true);
		try {
			updateVehicleInLocalState(
				selectedVehicle,
				editingVehicle,
				vehicles,
				setFieldValue,
				moment,
				DATE_FORMAT,
				message,
				handleModalClose
			);
		} catch (error) {
			message.error(error.message || 'Failed to update vehicle');
		} finally {
			setSavingVehicle(false);
		}
	};

	const handleRemoveVehicle = async () => {
		if (disabled) {
			return;
		}

		if (!selectedVehicle) {
			message.error('No vehicle selected');
			return;
		}

		const selectedVehicleId = selectedVehicle.id;
		const isPersisted = isPersistedVehicle(selectedVehicle);

		setSavingVehicle(true);

		try {
			if (isPersisted) {
				const response = await dispatch(
					updateVehicleV2({
						req_data: {
							...transformVehicleToAPIFormat(selectedVehicle, vendorId),
							id: selectedVehicleId,
							is_active: false,
						},
						setErrors: (errors) =>
							console.error(
								'[VehicleInformationStep] Remove vehicle errors:',
								errors
							),
					})
				);

				if (!response || response.status !== 'success') {
					const errorMessage =
						response?.error?.message || response?.message || 'Failed to remove vehicle';
					message.error(errorMessage);
					return;
				}
			}

			const updatedVehicles = vehicles.filter(
				(vehicle) => vehicle.key !== selectedVehicle.key
			);
			setFieldValue('vehicles', updatedVehicles);
			message.success('Vehicle removed successfully');
			handleModalClose();
		} catch (error) {
			console.error('[VehicleInformationStep] Error removing vehicle:', error);
			message.error(error?.message || 'Failed to remove vehicle');
		} finally {
			setSavingVehicle(false);
		}
	};

	// Document preview handlers
	const handleDocumentPreview = (fileKey, fileName = '') => {
		if (!fileKey) {
			message.warning('Document not available');
			return;
		}

		setPreviewFileKey(fileKey);
		setPreviewFileName(fileName);
		setFilePreviewVisible(true);
	};

	const handleFilePreviewClose = () => {
		setFilePreviewVisible(false);
		setPreviewFileKey(null);
		setPreviewFileName('');
	};

	return {
		// State
		modalVisible,
		selectedVehicle,
		editingVehicle,
		setEditingVehicle,
		filePreviewVisible,
		previewFileKey,
		previewFileName,
		savingVehicle,
		vehicleTypeOptions,
		loadingVehicleTypes,
		vehicles,
		uploading,
		rcDocFile,
		setRcDocFile,
		insuranceFile,
		setInsuranceFile,
		pucFile,
		setPucFile,
		consentLetterFile,
		setConsentLetterFile,
		bulkUploadModalVisible,
		bulkUploadLoading,
		// Refs
		rcDocFileInputRef,
		insuranceFileInputRef,
		pucDocFileInputRef,
		consentLetterFileInputRef,
		// Handlers
		handleFileSelectClick,
		handleFileChangeEvent,
		handleFileUploadClick,
		handleFileRemove,
		handleBulkUpload,
		handleBulkUploadFileUpload,
		handleBulkUploadSubmit,
		handleBulkUploadModalClose,
		handleAddVehicle,
		handleViewVehicle,
		handleModalClose,
		handleSaveChanges,
		handleRemoveVehicle,
		handleDocumentPreview,
		handleFilePreviewClose,
	};
}
